import { API_SERVERS, type ServerKey } from './apiConfig';
import type { ErrorData, FetchError } from './commonType';

function removeEmptyParams<T extends Record<string, unknown>>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => value !== '' && value !== undefined),
  ) as Partial<T>;
}

interface CustomFetchResponse<T> extends Response {
  code: number;
  message: string;
  data: T;
}

function resolveErrorMessage(status: number): string | undefined {
  switch (status) {
    case 400:
      return '잘못된 요청입니다.';
    case 401:
      return '인증되지 않은 요청입니다.';
    case 403:
      return '권한이 없는 요청입니다.';
    case 404:
      return '존재하지 않는 요청경로입니다.';
    case 500:
      return '서버 오류가 발생했습니다.';
    case 503:
      return '서버 점검 중입니다.';
  }
}

export function createFetch(serverKey: ServerKey) {
  const config = API_SERVERS[serverKey];

  const baseUrl = globalThis.window === undefined ? config.serverUrl : config.clientPath;

  return async function customFetch<T>(
    path: string,
    originalOptions: RequestInit & {
      params?: Record<string, string | number | boolean | null | undefined>;
    } = {},
    retryCount = 0,
  ): Promise<CustomFetchResponse<T>> {
    const options = { ...originalOptions };

    let url = `${baseUrl}${path}`;

    const defaultHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(globalThis.window === undefined && config.token ? { Authorization: `Bearer ${config.token}` } : {}),
    };

    // URL 쿼리 파라미터 처리
    if (options.params) {
      const cleanParams = removeEmptyParams(options.params);
      const searchParams = new URLSearchParams(cleanParams as Record<string, string>);
      url += `?${searchParams.toString()}`;
      delete options.params;
    }

    // Body 데이터 처리 (POST 요청 등)
    if (
      options.headers &&
      (options.headers as Record<string, string>)['Content-Type'] !== 'multipart/form-data' &&
      options.body &&
      typeof options.body === 'object'
    ) {
      options.body = JSON.stringify(removeEmptyParams(options.body as unknown as Record<string, unknown>));
    }

    const mergedHeaders = { ...defaultHeaders, ...options.headers };

    if ((mergedHeaders as Record<string, string>)['Content-Type'] === 'multipart/form-data') {
      delete (mergedHeaders as Record<string, string>)['Content-Type'];
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers: mergedHeaders,
      });
      if (!response.ok) {
        const errorData = (await response.json()) as ErrorData;
        const error = new Error(`에러가 발생했습니다.\n status: ${response.status}`) as FetchError<ErrorData>;
        error.status = response.status;
        errorData.result = resolveErrorMessage(response.status);
        error.data = errorData;
        throw error;
      }
      return response.json() as Promise<CustomFetchResponse<T>>;
    } catch (error) {
      if ((error as Error & { status?: number }).status === 401 && retryCount === 0) {
        return customFetch<T>(path, originalOptions, retryCount + 1);
      }
      throw error;
    }
  };
}
